import React, { useRef, useState } from "react";
import styled from "styled-components";
import ReCAPTCHA from "react-google-recaptcha";
import { makePostRequest } from "../../services/httpHelper";
import ErrorHandler from "../../services/ErrorHandler";
import { isEmail, isEmpty, isMobile } from "../../utils/formValidator";
import { CircularProgress } from "@material-ui/core";
import { Helmet } from "react-helmet-async";
import { RECAPTCHA_SITE_KEY } from "../../constants/config";

const Contact = () => {
  const [values, setValues] = useState({
    contactName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [recaptchaValid, setRecaptchaValid] = useState(false);

  const recaptchaRef = useRef();

  const handleChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  const handleRecaptcha = () => {
    setRecaptchaValid(true);
  };

  const validate = () => {
    const newErrors = {};

    if (isEmpty(values.contactName)) {
      newErrors.contactName = "Enter your full name";
    } else if (values.contactName.length < 5) {
      newErrors.contactName = "Name is too short";
    }

    if (isEmpty(values.email)) {
      newErrors.email = "Enter your email address";
    } else if (!isEmail(values.email)) {
      newErrors.email = "Invalid email address";
    }

    if (isEmpty(values.subject)) {
      newErrors.subject = "Please enter subject";
    }

    if (isEmpty(values.phone)) {
      newErrors.phone = "Enter your mobile number";
    } else if (!isMobile(values.phone)) {
      newErrors.phone = "Invalid mobile number";
    }

    if (isEmpty(values.message)) {
      newErrors.message = "Enter your message";
    } else if (values.message.length < 10) {
      newErrors.message = "Message is too short";
    }

    return newErrors;
  };

  const sendMessage = async () => {
    setErrors({});
    setServerError(null);
    setIsSubmitting(true);

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    if (!recaptchaValid) {
      setServerError("Please complete the reCAPTCHA challenge.");
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await makePostRequest("contact-us", values);
      if (res && res.errorType) {
        const errorMessage = ErrorHandler(res);
        setServerError(errorMessage);
      } else {
        setSubmitted(true);
        setValues({
          contactName: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      }
    } catch (err) {
      setServerError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact us for information on CS church management software</title>
        <meta
          name="description"
          content="Have questions or need assistance? Contact us today to learn more about CS Church Management Software."
        />
        <link rel="canonical" href="/contact-us" />
      </Helmet>

      <Wrapper id="contact">
        <div className="container">
          <Title>Contact Us</Title>
          <SubTitle>
            Have questions or need assistance? Our team is here to help. Contact us today to learn more about CS Church Management.
          </SubTitle>

          <Form>
            {isSubmitting && <CircularProgress />}
            {submitted && <SuccessMsg>Thank you, {values.contactName || "dear user"}. We'll get back to you soon.</SuccessMsg>}
            {serverError && <ErrorMsg>{serverError}</ErrorMsg>}

            <input
              type="text"
              name="contactName"
              placeholder="Full Name"
              value={values.contactName}
              onChange={handleChange}
            />
            {errors.contactName && <ErrorMsg>{errors.contactName}</ErrorMsg>}

            <input
              type="text"
              name="email"
              placeholder="Email Address"
              value={values.email}
              onChange={handleChange}
            />
            {errors.email && <ErrorMsg>{errors.email}</ErrorMsg>}

            <input
              type="text"
              name="phone"
              placeholder="Mobile Phone Number"
              value={values.phone}
              onChange={handleChange}
            />
            {errors.phone && <ErrorMsg>{errors.phone}</ErrorMsg>}

            <input
              type="text"
              name="subject"
              placeholder="Subject"
              value={values.subject}
              onChange={handleChange}
            />
            {errors.subject && <ErrorMsg>{errors.subject}</ErrorMsg>}

            <textarea
              name="message"
              rows="5"
              placeholder="Message"
              value={values.message}
              onChange={handleChange}
            />
            {errors.message && <ErrorMsg>{errors.message}</ErrorMsg>}

            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={handleRecaptcha}
            />

            <SubmitButton
              type="button"
              onClick={sendMessage}
              disabled={!recaptchaValid || isSubmitting}
            >
              Send Message
            </SubmitButton>
          </Form>
        </div>
      </Wrapper>
    </>
  );
};

export default Contact;
const Wrapper = styled.section`
  padding: 80px 20px;
  background: #f9fafb;
`;

const Title = styled.h2`
  font-size: 30px;
  font-weight: 800;
  text-align: center;
  margin-bottom: 10px;
`;

const SubTitle = styled.p`
  max-width: 600px;
  margin: auto;
  text-align: center;
  font-size: 16px;
  color: #555;
  margin-bottom: 40px;
`;

const Form = styled.form`
  max-width: 600px;
  margin: auto;
  display: grid;
  gap: 20px;

  input,
  textarea {
    padding: 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    width: 100%;
  }

  textarea {
    resize: vertical;
  }
`;

const SubmitButton = styled.button`
  background-color: #f97316;
  color: white;
  padding: 15px;
  border: none;
  font-weight: 600;
  font-size: 16px;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background-color: #ea580c;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorMsg = styled.p`
  color: #dc2626;
  font-size: 13px;
  margin: -10px 0 10px 0;
`;

const SuccessMsg = styled.p`
  color: #16a34a;
  font-size: 15px;
  font-weight: 600;
`;
